/* eslint-disable @typescript-eslint/no-explicit-any */
import { useLoaderStore } from '@/stores/utils/loader.store';

export interface ActionHelpers {
  startLoading: <T>(loaderName: string, callback: Promise<T>) => Promise<T>;
  endLoading: (loaderName: string) => void;
}

export const createActionHelpers = (): ActionHelpers => {
  return {
    async startLoading<T>(loaderName: string, callback: Promise<T>) {
      const loaderStore = useLoaderStore();
      loaderStore.start(loaderName);
      if (loaderName === 'boq' || loaderName === 'boq-variation-update') {
        loaderStore.setBOQLoader(true);
      }

      try {
        const response = await callback;
        loaderStore.end(loaderName);
        if (loaderName === 'boq' || loaderName === 'boq-variation-update') {
          loaderStore.setBOQLoader(false);
        }
        return response;
      } catch (error: any) {
        loaderStore.end(loaderName);
        if (loaderName === 'boq' || loaderName === 'boq-variation-update') {
          loaderStore.setBOQLoader(false);
        }
        throw error.response;
      }
    },
    endLoading(loaderName: string) {
      const loaderStore = useLoaderStore();
      loaderStore.end(loaderName);
      if (loaderName === 'boq' || loaderName === 'boq-variation-update') {
        loaderStore.setBOQLoader(false);
      }
    },
  };
};

/**
 * Awaits a {@link ActionHelpers.startLoading} call for its side effects, absorbing a failure the
 * caller can't do anything about.
 *
 * `startLoading` rethrows, but by the time it does, the axios interceptor has already reported the
 * failure — `processCustomError` raises the toast and `endAll()` clears the loaders — so a view has
 * nothing left to do except carry on. Left unhandled the rejection surfaces as an unhandled promise
 * rejection, and inside an `await` sequence it also abandons every step after it, which is how a
 * form ends up half-applied: one field set from a response that arrived, the rest never assigned.
 *
 * Resolves to `undefined` on failure, so a caller that *does* care can tell the difference. Reach
 * for it where the next step should happen regardless — not to silence an error that matters.
 */
export const settleLoading = async <T>(request: Promise<T>): Promise<T | undefined> => {
  try {
    return await request;
  } catch {
    return undefined;
  }
};
